import type { Comic } from '@tanko/shared'
import { sortComics, type ComicSortOrder } from './comic-sort'
import { isBackedUp } from './comic-sync'

/**
 * 書庫的一列。
 *
 * 書庫只有一份清單，內容是雲端目錄與本機尚未註冊作品的聯集。兩邊都有的那些合成同一列
 * ——它們本來就是同一本作品，差別只在這台裝置有沒有它的檔案。
 */
export type LibraryEntry = {
  /**
   * 統一識別碼：已註冊用雲端目錄識別碼，尚未註冊用本機識別碼。
   *
   * 選取、範圍選取與批次動作全部建立在這個值上，因此一列恰好對應一個識別碼，
   * 刪除的語意不會因為「這一本算本地的還是雲端的」而含糊。
   */
  id: string
  /** 顯示用的那一份。雲端有這一本就以雲端為準——不管從哪台裝置上傳，metadata 都在那裡。 */
  comic: Comic
  /** 本機那一份。有值代表這台裝置持有檔案，備份、以本地檔案閱讀、本機刪除都要用到它。 */
  local?: Comic
  backedUp: boolean
  /** 本機那一份現在讀不讀得出來：索引完成、檔案還在、來源已授權。 */
  localReadable: boolean
  /** 這台裝置現在開不開得起來。本機檔案可用，或者雲端已經備份完成。 */
  readable: boolean
  /** 排序沿用 sortComics，因此把它需要的兩個欄位攤在這一層。取自 comic。 */
  title: string
  addedAt: number
}

/** 一本作品在這一列裡的識別碼。已註冊的作品在任何裝置上都得到同一個值。 */
export const entryId = (comic: Pick<Comic, 'id' | 'cloudComicId'>): string => comic.cloudComicId || comic.id

function toEntry(comic: Comic, local: Comic | undefined, locallyReadable: boolean): LibraryEntry {
  const backedUp = isBackedUp(comic)
  return {
    id: entryId(comic),
    comic,
    local,
    backedUp,
    localReadable: locallyReadable,
    readable: locallyReadable || backedUp,
    title: comic.title,
    addedAt: comic.addedAt,
  }
}

/**
 * 把雲端清單與本機清單合併成一份。
 *
 * 配對鍵是雲端目錄識別碼：本機那一份只要已經拿到識別碼，就與雲端同一本合成一列，因此
 * 同一本作品不會出現兩次。雲端沒有的（尚未註冊、或註冊後雲端清單還沒重新抓）由本機那一份
 * 自成一列，否則剛加入的作品會在畫面上消失一段時間。
 *
 * `locallyReadable` 由呼叫端提供：能不能讀一本本機作品同時取決於來源授權、索引是否完成、
 * 以及檔案還在不在，那些都是 store 的狀態，不屬於這個純函式。
 */
export function mergeLibraryEntries(
  cloud: readonly Comic[],
  local: readonly Comic[],
  locallyReadable: (comic: Comic) => boolean,
  order: ComicSortOrder,
): LibraryEntry[] {
  const localByCloudId = new Map<string, Comic>()
  for (const comic of local) if (comic.cloudComicId) localByCloudId.set(comic.cloudComicId, comic)

  const entries: LibraryEntry[] = []
  const paired = new Set<string>()
  for (const comic of cloud) {
    const mine = localByCloudId.get(comic.id)
    if (mine) paired.add(mine.id)
    entries.push(toEntry(comic, mine, !!mine && locallyReadable(mine)))
  }
  for (const comic of local) {
    if (paired.has(comic.id)) continue
    entries.push(toEntry(comic, comic, locallyReadable(comic)))
  }
  return sortComics(entries, order)
}
